import React, { useState, useEffect, useRef } from "react";
import ApiManager from "../ApiManager/ApiManager";
import MapView from "./MapView/MapView";
import ManualAddressForm from "./ManualAddressForm/ManualAddressForm";
import { LoadingSpinner } from "./LoadingSpinner/LoadingSpinner";
import "./MapInput.css";

const MapInput = ({ 
  label = "Address", 
  onAddressSelect, 
  initialValue,
  required = false,
  name = "address",
  validateValue = null
}) => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMap, setViewMap] = useState(true);
  const [shouldFetchSuggestions, setShouldFetchSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState({
    latitude: 0,
    longitude: 0,
    description: "",
  });
  const [error, setError] = useState("");
  
  // Use ref to track if we've processed initialValue
  const initialProcessedRef = useRef(false);

  const [manualAddress, setManualAddress] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
  });

  const handleManualAddressChange = (name, value) => {
    const updatedAddress = {
      ...manualAddress,
      [name]: value,
    };
    setManualAddress(updatedAddress);
    setError("");
    
    if (onAddressSelect) {
      onAddressSelect(updatedAddress);
    }
  };

  const toggleView = () => {
    // Toggle between map and manual entry
    setViewMap(prev => !prev);
    console.log("Toggling view to:", !viewMap);
  };

  const [debouncedInput, setDebouncedInput] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInput(inputValue);
    }, 300);
    return () => clearTimeout(handler);
  }, [inputValue]);

  useEffect(() => {
    if (debouncedInput && shouldFetchSuggestions)
      fetchSuggestions(debouncedInput);
  }, [debouncedInput]);

  // Fix the infinite update loop in initialValue effect
  useEffect(() => {
    // Skip if no initialValue or if we've already processed it with the same values
    if (!initialValue) return;
    
    // Check if we're dealing with map coordinates or manual address
    const isMapCoordinates = initialValue.latitude !== undefined && 
                          initialValue.longitude !== undefined &&
                          initialValue.latitude !== null && 
                          initialValue.longitude !== null;
    
    if (isMapCoordinates) {
      setViewMap(true);
      setSelectedLocation({
        latitude: initialValue.latitude || 0,
        longitude: initialValue.longitude || 0,
        description: initialValue.description || "",
      });
      setInputValue(initialValue.description || "");
    } else {
      // Manual address
      setViewMap(false);
      setManualAddress(initialValue);
    }
    
    // Only call onAddressSelect during initial setup, not on every render
    if (!initialProcessedRef.current) {
      initialProcessedRef.current = true;
    }
  }, [initialValue?.latitude, initialValue?.longitude, initialValue?.description]);

  const fetchSuggestions = async (text) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        input: text,
        location: "28.5561,77.1", // Optional: can be prop later
        radius: 50,
        strictbounds: false,
      });
      const response = await ApiManager.getAddressSuggestions(params);
      const data = await response.json();
      setSuggestions(data.predictions || []);
    } catch (err) {
      console.error("Error fetching suggestions:", err);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (suggestion) => {
    if (!suggestion || !suggestion.geometry || !suggestion.geometry.location) {
      console.error("Invalid suggestion format:", suggestion);
      return;
    }

    const location = {
      latitude: suggestion.geometry.location.lat || 0,
      longitude: suggestion.geometry.location.lng || 0,
      description: suggestion.description || "",
    };
    
    setSelectedLocation(location);
    setInputValue(suggestion.description || "");
    setSuggestions([]);
    setShouldFetchSuggestions(false);
    setError("");
    
    if (onAddressSelect) {
      onAddressSelect(location);
    }
  };

  const handleInputChange = (e) => {
    setShouldFetchSuggestions(true);
    setInputValue(e.target.value);
    
    if (e.target.value) {
      setError("");
    } else if (required) {
      setError(`${label} is required`);
    }
  };

  const validateInput = () => {
    if (required) {
      if (viewMap) {
        // In map view, validation happens on selectedLocation or inputValue
        if (!inputValue) {
          setError(`${label} is required`);
          return false;
        }
      } else {
        // In manual mode, validate the address fields
        if (!manualAddress.addressLine1 || !manualAddress.city || !manualAddress.state) {
          setError(`${label} fields are required`);
          return false;
        }
      }
    }
    
    // If there's a custom validation function, use it
    if (validateValue) {
      const customError = validateValue(viewMap ? selectedLocation : manualAddress);
      if (customError) {
        setError(customError);
        return false;
      }
    }
    
    return true;
  };

  // Add blur handler for validation
  const handleBlur = () => {
    validateInput();
  };

  return (
    <div className="mapInput">
      <div className="mapSection">
        <div className="mapHeader">
          <label className={required ? "required-label" : ""}>
            {label} {required && <span className="required-mark">*</span>}
          </label>
          <button
            type="button"
            className="custom-button"
            onClick={toggleView}
          >
            {viewMap ? "Switch to Manual Entry" : "Switch to Map Entry"}
          </button>
        </div>

        {viewMap ? (
          <div className="manualEntryForm">
            <div className="address-input-container">
              <input
                type="text"
                placeholder={`Enter ${label}${required ? ' *' : ''}`}
                value={inputValue}
                className={`input ${error ? 'input-error' : ''}`}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required={required}
                name={name}
                data-testid={`${name}-input`}
              />
              {isLoading && (
                <div className="loading-indicator">
                  <LoadingSpinner 
                    overlay={false}
                    size="small"
                    message="Loading suggestions..."
                    showProgress={false}
                  />
                </div>
              )}
              {suggestions.length > 0 && (
                <ul className="suggestions-list">
                  {suggestions.map((s, i) => (
                    <li
                      key={i}
                      className="suggestion-item"
                      onClick={() => handleSelect(s)}
                    >
                      {s.description}
                    </li>
                  ))}
                </ul>
              )}
              {error && <div className="error-message">{error}</div>}
            </div>
            
            {selectedLocation.latitude && selectedLocation.longitude ? (
              <MapView
                latitude={selectedLocation.latitude}
                longitude={selectedLocation.longitude}
                zoom={20}
                height={300}
              />
            ) : (
                <p>No location selected</p>
            )}
            
            {/* Hidden input for form validation */}
            {required && (
              <input 
                type="hidden" 
                name={`${name}-validation`}
                required
                value={inputValue ? "valid" : ""}
              />
            )}
          </div>
        ) : (
          <ManualAddressForm
            formData={manualAddress}
            onChange={handleManualAddressChange}
            required={required}
            onBlur={handleBlur}
            error={error}
            name={name}
          />
        )}
        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default MapInput;
